import React, { memo, useCallback, useState } from 'react';
import { AlertCircle, ToggleLeft, ToggleRight } from 'lucide-react';

import BoxTypeSection from 'components/logistics/fulfillmentLifecycle/modals/tabs/BoxTypeSection';
import { Button } from 'shadcn/components/ui/Button';
import { Skeleton } from 'shadcn/components/ui/Skeleton';

/**
 * Number of skeleton rows to show while loading.
 */
const SKELETON_COUNT = 3;

/**
 * StatusBreakdownTab — container for the Status Breakdown modal tab.
 *
 * Manages the Order/Shipment ID toggle and renders one BoxTypeSection per box
 * type returned by the breakdown API.  Handles loading, error, and empty states.
 *
 * @param {Object} props
 * @param {Array|null|undefined} props.breakdowns - Array of box-type breakdown objects
 * @param {boolean} props.loading - Whether data is currently being fetched
 * @param {string|null} props.error - Error message, if any
 */
function StatusBreakdownTab({ breakdowns, error, loading }) {
    const [idMode, setIdMode] = useState('order');

    const toggleIdMode = useCallback(() => {
        setIdMode((prev) => (prev === 'order' ? 'shipment' : 'order'));
    }, []);

    // --- Loading state ---
    if (loading) {
        return (
            <div className="space-y-4 py-4" data-testid="status-breakdown-loading">
                {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                    <Skeleton key={i} className="h-48 w-full rounded-lg" />
                ))}
            </div>
        );
    }

    // --- Error state ---
    if (error) {
        return (
            <div className="flex flex-col items-center gap-2 py-8 text-center" data-testid="status-breakdown-error">
                <AlertCircle className="h-8 w-8 text-destructive" />
                <p className="text-sm font-medium">Error loading status breakdown</p>
                <p className="text-xs text-muted-foreground">{error}</p>
            </div>
        );
    }

    // --- Empty state ---
    if (!breakdowns || breakdowns.length === 0) {
        return (
            <div className="py-8 text-center" data-testid="status-breakdown-empty">
                <p className="text-sm text-muted-foreground">No breakdown data available for this route.</p>
            </div>
        );
    }

    // --- Data state ---
    return (
        <div className="space-y-4 py-4" data-testid="status-breakdown-content">
            {/* Instructional text + toggle */}
            <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                    Click on a status row to expand and view individual orders. Toggle between Order ID and Shipment ID
                    views.
                </p>

                <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5 text-xs"
                    onClick={toggleIdMode}
                    aria-label={`Showing ${idMode === 'order' ? 'Order' : 'Shipment'} IDs`}
                >
                    {idMode === 'order' ? <ToggleLeft className="h-4 w-4" /> : <ToggleRight className="h-4 w-4" />}
                    {idMode === 'order' ? 'Order IDs' : 'Shipment IDs'}
                </Button>
            </div>

            {/* Box type sections */}
            {breakdowns.map((breakdown) => (
                <BoxTypeSection
                    key={breakdown.boxType}
                    boxType={breakdown.boxType}
                    total={breakdown.total}
                    remaining={breakdown.remaining}
                    statuses={breakdown.statuses}
                    idMode={idMode}
                />
            ))}
        </div>
    );
}

export default memo(StatusBreakdownTab);
